import * as React from 'react';
import { API, idCreateRegex, propertyNameRegex, FUNC_NAME_GET_ALL_LABELS, COPILOT_ACTION_KEYS, PAGE_TYPE } from '@config/index';
import {
  FrameType,
  ProviderType,
  DataQuery,
  FunctionParameter,
  Attribute,
  FrameItem,
  Connection,
  connectionToLocal,
  ReferenceNode,
  TrainingPhraseConfig,
  ParameterType,
  TrainingPhraseOwnerType,
  TrainingPhraseType,
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  GenericType,
  ExternalType,
  KeyValuePair
} from '@interface/index';
import {
  CaretDownOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Popover, Popconfirm, Switch, Spin, message, Space, Form, Menu } from 'antd';
import { cloneDeep, debounce, uniqueId } from 'lodash';
import {
  NiModal,
  NiSelect,
  TrainingPhraseComp,
  NiTable,
  NiCascader,
  NiInput,
  NiForm,
  NiFormItem,
  discardModal,
  NiRadio,
  CommentPopover,
  NiDragableTable,
  HelperPopover,
  NiTabs
} from '@component/index';
import { ReferenceTextArea } from '@component/reference-textarea';
import { TypeSelect } from './modal-comps';
import { FormattedMessage } from 'react-intl';
import { localizedString } from '@locale/index';
import {
  ParameterMapStore,
  isEqual,
  getAgentId,
  getUserId,
  isStructAgent,
  getFullName,
  getOrgInfo,
  getAgentInfo,
  instance,
  getFormattedName
} from '@lib/index';
import { SqlInput } from '@component/sql/sql-input';

export interface DbModalProps {
  disabled?: boolean;
  visible: boolean;
  title: string;
  frame: FrameItem;
  availableIntents: IntentInfo[];
  availableComposites: CompositeInfo[];
  availableEntities: EntityInfo[];
  genericTypes: GenericType[];
  externalTypes: ExternalType[];
  data: DataQuery;
  selectedParameters: Attribute[];
  providerType?: ProviderType;
  confirm: (next: DataQuery) => void;
  close: () => void;
}

export interface DbModalState {
  providerType?: ProviderType;
  kotlin: boolean;
  schedule?: boolean;
  cron?: string;
  sqlQuery: string;
  functionMeta: KeyValuePair[];
  restfulMethod: string;
  restfulURL: string;
  restfulHeaders?: KeyValuePair[];
  postBody: string;
  nativeBody: string;
  bodyComment?: string;
  outputName?: string;
  comment?: string;
  multiValue: boolean;
  returnNullable: boolean;
  isStatic: boolean;
  errorMsgs?: string[];
  ctxParams?: Attribute[];
  inputParameters: FunctionParameter[];
  frame?: ReferenceNode;
  allowTrigger: boolean;
  triggerExpressions?: TrainingPhraseConfig[];
  contractDisabled: boolean;
  implementationDisabled: boolean;
  loading: boolean;
  tab: 'schema' | 'implementation';
}

export class DbModal extends React.Component<DbModalProps, DbModalState> {

  constructor(props: DbModalProps) {
    super(props);

    const { providerType: defaultProviderType, availableIntents, availableComposites, frame, availableEntities, data, genericTypes, externalTypes, disabled } = props;


    const providerType = defaultProviderType || (frame.type === FrameType.service ? ProviderType.Restful : ProviderType.Native);

    this.state = {
      providerType,
      tab: 'schema',
      loading: false,
      kotlin: data.kotlin,
      schedule: data.schedule,
      cron: data.databaseConfig && data.databaseConfig.cron,
      sqlQuery: (providerType === ProviderType.Postgrest || providerType === ProviderType.GoogleSheets || providerType === ProviderType.Supabase)
        && data.databaseConfig ? data.databaseConfig.query : '',
      functionMeta: providerType === ProviderType.GoogleSheets &&
        data.databaseConfig && data.databaseConfig.functionMeta || [],
      restfulMethod: providerType === ProviderType.Restful &&
        data.restfulConfig && data.restfulConfig.method || 'POST',
      restfulURL: providerType === ProviderType.Restful &&
        data.restfulConfig && (data.restfulConfig.url || data.restfulConfig.path) || '',
      restfulHeaders: providerType === ProviderType.Restful &&
        data.restfulConfig && data.restfulConfig.headers || [],
      postBody: providerType === ProviderType.Restful
        && data.restfulConfig ? data.restfulConfig.postBody || '' : '',
      nativeBody: providerType === ProviderType.Native
        && data.functionConfig
        && data.functionConfig.body || '',
      bodyComment: data.bodyComment,
      outputName: data.name,
      comment: data.comment,
      inputParameters: data.parameters || [],
      frame: data.frame,
      isStatic: providerType === ProviderType.Native &&
        data.functionConfig &&
        data.functionConfig.isStatic || false,
      multiValue: data.multiValue,
      returnNullable: !data.disableReturnNull,
      errorMsgs: data.errorMsgs,
      allowTrigger: data.allowTrigger,
      triggerExpressions: data.triggerExpressions,
      contractDisabled: disabled ||
        !!data.originKey ||
        !!data.inheritedFromCompositeId ||
        data.isOverwrite || false,
      implementationDisabled: disabled || false
    };
  }

  implementationListener: any;
  componentDidMount() {

    this.implementationListener = instance.on(COPILOT_ACTION_KEYS.SET_IMPLEMENTATION, (args: any) => {
      if (!args.type) {
        message.warning('parameter type is required');
        return;
      }

      if (!args.type || ['providerDependent', 'kotlin'].indexOf(args.type as any) === -1) {
        message.warning('invalid type');
        return;
      }
      this.setState({ kotlin: args.type === 'kotlin' });
    });
  }

  componentWillUnmount() {
    this.implementationListener();
  }

  render() {
    const props = this.props;
    const { disabled } = props;
    const { loading, tab } = this.state;

    return (
      <NiModal
        open={this.props.visible}
        width={'60%'}
        style={{ top: 100 }}
        centered={false}
        wrapClassName="data-query-modal-wrapper" // 应用自定义样式类名
        title={
          <div className="data-query-header">
            <h3
              style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}
            >
              Function
            </h3>
            <Menu
              className="data-query-tab-menu"
              style={{ width: 300, marginLeft: 36 }}
              onClick={(e: any) => {
                this.setState({ tab: e.key as any });
              }} selectedKeys={[tab]} mode="horizontal" items={[{
                key: 'schema',
                label: 'Schema'
              }, {
                key: 'implementation',
                label: 'Implementation'
              }]} />
          </div>
        }
        okText={(this.props.data.inheritedFromCompositeId) &&
          !(this.props.frame as any).isInterface &&
          <Popconfirm
            title={
              <span>{localizedString('data_query_inherit_copy_popconfirm')}
                <HelperPopover localizedKey="data_query_inherit_copy_popover" />
              </span>
            }
            onConfirm={this.handleOk}
          >
            <a href="#">{localizedString('common_confirm')}</a>
          </Popconfirm> || undefined
        }
        onOk={disabled ||
          (props.data.inheritedFromCompositeId &&
            !(props.frame as any).isInterface)
          ? undefined : () => {
            this.handleOk();
          }}
        okButtonProps={!!props.disabled ||
          (props.data.inheritedFromCompositeId && (props.frame as any).isInterface) ||
          props.data.name === FUNC_NAME_GET_ALL_LABELS
          ? {
            type: 'ghost',
            disabled: true
          } : undefined}
        onCancel={this.handleCancel}
      // closable={false}
      >
        <Spin spinning={loading}>
          {props.visible && <div className="db-modal">{this.bodyComp()}</div>}
        </Spin>
      </NiModal>
    );
  }

  dirtyCheck = () => {
    const { data, providerType } = this.props;
    const state = this.state;

    return !isEqual(data.parameters || [], state.inputParameters) ||
      ((providerType === ProviderType.Postgrest || providerType === ProviderType.GoogleSheets || providerType === ProviderType.Supabase) &&
        data.databaseConfig &&
        (data.databaseConfig.query !== state.sqlQuery ||
          !isEqual(data.databaseConfig.functionMeta, state.functionMeta ||
            data.databaseConfig.cron !== state.cron))
      ) ||
      (providerType === ProviderType.Restful && data.restfulConfig &&
        (data.restfulConfig.url !== state.restfulURL ||
          data.restfulConfig.postBody !== state.postBody ||
          data.restfulConfig.headers !== state.restfulHeaders ||
          data.restfulConfig.method !== state.restfulMethod ||
          data.restfulConfig.url !== state.restfulURL)) ||
      (providerType === ProviderType.Native
        && data.functionConfig
        && data.functionConfig.body !== state.nativeBody) ||
      data.name !== state.outputName ||
      (providerType === ProviderType.Native &&
        data.functionConfig &&
        (state.isStatic !== data.functionConfig.isStatic)) ||
      !isEqual(data.frame, state.frame) ||
      data.multiValue !== state.multiValue ||
      !isEqual(data.errorMsgs, state.errorMsgs) ||
      data.allowTrigger !== state.allowTrigger ||
      !isEqual(data.triggerExpressions, state.triggerExpressions) ||
      data.kotlin !== state.kotlin ||
      data.schedule !== state.schedule;
  };

  handleOk = debounce(() => {
    const {
      kotlin,
      schedule,
      cron,
      sqlQuery,
      functionMeta,
      restfulMethod,
      restfulURL,
      restfulHeaders,
      outputName,
      comment,
      inputParameters,
      frame,
      postBody,
      nativeBody,
      bodyComment,
      errorMsgs,
      multiValue,
      returnNullable,
      isStatic,
      allowTrigger,
      triggerExpressions,
      providerType
    } = this.state;
    const { data } = this.props;

    const owener = this.props.frame as any;


    if (providerType === ProviderType.Postgrest || providerType === ProviderType.GoogleSheets) {
      if ((!sqlQuery || !sqlQuery.length)) {
        message.warning(localizedString('database_code_placeholder'));

        return;
      }

      if (schedule) {
        const cronregex = new RegExp(/(@(annually|yearly|monthly|weekly|daily|hourly|reboot))|(@every (\d+(ns|us|µs|ms|s|m|h))+)|((((\d+,)+\d+|(\d+(\/|-)\d+)|\d+|\*) ?){5,7})/);

        if (!cron || !cronregex.test(cron)) {
          // if (!cron) {
          message.warning('invalid crontab string');
          return;
        }

      }
    }

    if (providerType === ProviderType.Restful && !kotlin) {
      if ((!restfulURL || !restfulURL.length)) {
        message.warning(localizedString('data_query_url_placeholder'));

        return;
      }
    }
    if (providerType === ProviderType.Native && !owener.isInterface && !owener.externalQualification) {
      if (!nativeBody) {
        message.warning(localizedString('database_native_body_placeholder'));

        return;
      }
    }

    if (!outputName) {
      message.warning(localizedString('data_query_name_placeholder'));

      return;
    }

    if ((data && data.name !== outputName || !data) && !propertyNameRegex.test(outputName)) {
      message.warning(localizedString('slot_label_error'));
      return;
    }


    if (!schedule && !frame) {
      message.warning(localizedString('data_query_frame_hint'));

      return;
    }


    for (const inputParam of inputParameters) {
      if (!inputParam.name.length || !idCreateRegex.test(inputParam.name)) {
        message.warning(localizedString('common_invalid_name'));
        return;
      }

      if (!inputParam.type) {
        message.warning('Parameter type is required!');
        return;
      }
    }

    const next = cloneDeep(data);

    delete next.databaseConfig;
    delete next.restfulConfig;
    delete next.functionConfig;

    if (providerType === ProviderType.Postgrest || providerType === ProviderType.GoogleSheets) {
      next.databaseConfig = {
        cron,
        functionMeta,
        query: sqlQuery,
      };

    } else if (providerType === ProviderType.Restful) {
      next.restfulConfig = {
        postBody,
        method: restfulMethod,
        url: restfulURL,
        path: '',
        headers: restfulHeaders
      };

    } else if (providerType === ProviderType.Native) {
      next.functionConfig = {
        isStatic,
        body: nativeBody
      };
    }

    // next.queryType = providerType;
    next.bodyComment = bodyComment;
    next.name = outputName || '';
    next.comment = comment;
    next.frame = frame;
    next.parameters = inputParameters.filter((item: FunctionParameter) =>
      !!item.type);
    next.errorMsgs = errorMsgs;
    next.multiValue = multiValue;
    next.disableReturnNull = !returnNullable;
    next.allowTrigger = allowTrigger;
    next.triggerExpressions = triggerExpressions;
    next.kotlin = kotlin;
    next.schedule = schedule;

    this.props.confirm(next);
  }, 500);

  handleCancel = () => {
    if (this.dirtyCheck()) {
      discardModal(() => {
        this.clearState();
        this.props.close();
      });
      return;
    }

    this.clearState();
    this.props.close();
  };

  /**
   * 输出表名
   */
  outputNameComp = () => {
    const state = this.state;
    const { data, disabled } = this.props;

    return <NiInput
      style={{ flex: 1 }}
      value={state.outputName || ''}
      disabled={state.contractDisabled}
      placeholder={localizedString('slot_label_hint')}
      onChange={(e: any) => {
        this.setState({ outputName: e.target.value });
      }}
    />;
  };

  multiValueComp = () => {
    const { data, disabled } = this.props;
    const { multiValue, triggerExpressions, contractDisabled } = this.state;

    return <Switch
      disabled={contractDisabled}
      checked={this.state.multiValue}
      onChange={(checked: boolean) => {

        this.setState({
          multiValue: checked
        });

        if (checked !== multiValue) {
          this.setState({
            triggerExpressions: (triggerExpressions || [])
              .map((tp: TrainingPhraseConfig) => ({
                ...tp,
                toBeDeleted: true
              }))
          });
        }
      }}
    />;
  };

  nullableComp = () => {
    const { returnNullable, contractDisabled } = this.state;

    return <Switch
      disabled={contractDisabled}
      checked={returnNullable}
      onChange={(checked: boolean) => {

        this.setState({
          returnNullable: checked
        });
      }}
    />;
  };


  staticComp = () => {
    const { data, disabled } = this.props;
    const { isStatic, contractDisabled } = this.state;

    return <Switch
      style={{ marginLeft: 36 }}
      disabled={contractDisabled}
      checked={isStatic}
      onChange={(checked: boolean) => {
        this.setState({
          isStatic: checked
        });
      }}
    />;
  };

  scheduleComp = () => {
    const { data, disabled } = this.props;
    const { schedule, cron } = this.state;

    return <Switch
      style={{ marginLeft: 36 }}
      disabled={disabled}
      checked={schedule}
      onChange={(checked: boolean) => {
        this.setState({
          schedule: checked
        });
      }}
    />;
  };

  cronComp = () => {
    const { data, disabled } = this.props;
    const { schedule, cron } = this.state;

    return schedule && <NiInput placeholder="Enter cron" value={cron} onChange={(e: any) => {
      this.setState({
        cron: e.target.value
      });
    }} /> || undefined;
  };


  /**
   * 数据库指令 或 post body
   */
  queryComp = () => {
    const state = this.state;
    const props = this.props;
    const { frame } = props;
    const { providerType, outputName, frame: returnType } = state;

    const { implementationDisabled, kotlin, inputParameters, bodyComment, sqlQuery } = this.state;

    let key = '';
    let placeholder = '';

    switch (providerType) {
      case ProviderType.Postgrest:
      case ProviderType.GoogleSheets:
      case ProviderType.Supabase:
        key = 'sqlQuery';
        placeholder = 'database_code_placeholder';
        break;
      case ProviderType.Restful:
        key = 'postBody';
        placeholder = 'database_post_body_placeholder';
        break;
      case ProviderType.Native:
        key = 'nativeBody';
        placeholder = 'database_native_body_placeholder';
        break;
      default:
        break;
    }

    const rawCodeType = cloneDeep(frame);

    if (frame.type === FrameType.service) {

      if (!!frame.originKey) {
        const s = frame && frame.source && frame.source.split('.');
        rawCodeType.name = s && s[s?.length - 1];
      } else {
        const agent = getAgentInfo();
        rawCodeType.name = agent && agent.name;
      }
    }

    return ((providerType === ProviderType.Postgrest || providerType === ProviderType.GoogleSheets) &&
      !kotlin)
      ? <SqlInput
        sql={sqlQuery}
        rawCodeMethodName={outputName}
        rawCodeReturn={returnType?.name}
        rawCodeParams={inputParameters}
        returnMultiValue={state.multiValue}
        returnNullable={state.returnNullable}
        onSqlChange={(next: string) => {
          this.setState({ sqlQuery: next });
        }} />
      : <ReferenceTextArea
        key={`${kotlin}`}
        rawCodeMode={providerType === ProviderType.Native || providerType === ProviderType.Restful || kotlin}
        rawCodeMethodName={outputName}
        rawCodeReturn={returnType?.name}
        disabled={implementationDisabled}
        rows={10}
        customSuggestions={[]}
        className="refer-input"
        data={(state as any)[key] as string}
        placeholder={localizedString(placeholder)}
        style={{
          flex: 1,
          minHeight: '144px',
          height: '60vh',
        }}
        allowNewLine={true}
        forPSQL={(frame as any).psql}
        parameterMap={ParameterMapStore.all()}
        initParameters={this.getReferParameters()}
        // suggestions={inputParameters.map((p: FunctionParameter) => p.name)}
        update={(query: string) => {
          const next = { [key]: query };
          this.setState(next as any);
        }}
        hideComment={providerType === ProviderType.Restful}
        comment={bodyComment}
        onCommentChange={(next: string) => {
          this.setState({ bodyComment: next });
        }}
        rawCodeType={(providerType === ProviderType.Native || providerType === ProviderType.Restful || kotlin) && rawCodeType || undefined}
        rawCodeLang={(providerType === ProviderType.Restful && !kotlin) ? 'text' : undefined}
        rawCodeParams={inputParameters}
      />;
  };

  /**
   * WebHook URL
   */
  restfulURLComp = () => {
    const state = this.state;
    const props = this.props;
    const { implementationDisabled, restfulURL } = state;
    return <NiInput
      className="url-input"
      disabled={implementationDisabled}
      addonBefore={<NiSelect
        disabled={implementationDisabled}
        value={state.restfulMethod}
        onChange={(value: string) => {
          this.setState({ restfulMethod: value });
        }}
        style={{ width: 100 }}
      >
        <NiSelect.Option key="get" value="GET">GET</NiSelect.Option>
        <NiSelect.Option key="post" value="POST">POST</NiSelect.Option>
        <NiSelect.Option key="patch" value="PATCH">PATCH</NiSelect.Option>
        <NiSelect.Option key="put" value="PUT">PUT</NiSelect.Option>
        <NiSelect.Option key="delete" value="DELETE">DELETE</NiSelect.Option>
      </NiSelect>}
      value={restfulURL}
      placeholder={localizedString('data_query_url_placeholder')}
      onChange={(e: any) => {
        this.setState({ restfulURL: e.target.value });
      }}
    />;

    // <ReferenceTextArea
    //   disabled={implementationDisabled}
    //   rows={1}
    //   className="refer-input  reset restful"
    //   data={state.restfulURL}
    //   placeholder={localizedString('data_query_url_placeholder')}
    //   style={{
    //     flex: 1
    //   }}
    //   parameterMap={ParameterMapStore.all()}
    //   initParameters={this.getReferParameters()}
    //   update={(query: string) => {
    //     this.setState({ restfulURL: query });
    //   }}
    // />;
  };

  headerComp = () => {
    const { disabled } = this.props;
    const { restfulHeaders } = this.state;
    return <NiTable
      pagination={false}
      rowKey="index"
      dataSource={restfulHeaders &&
        restfulHeaders.map((item: KeyValuePair, index: number) => ({
          ...item,
          index
        })) || []}
      style={{ flex: 1 }}
      columns={[
        {
          title: 'Key',
          dataIndex: 'key',
          render: (item: string, record: KeyValuePair, index: number) =>
            <NiInput
              disabled={disabled}
              onChange={(e: any) => {

                const next = cloneDeep(restfulHeaders) || [];

                record.key = e.target.value;
                next[index] = record;
                this.setState({ restfulHeaders: next });
              }}
              value={item}
            />
        },
        {
          title: 'Value',
          dataIndex: 'value',
          render: (item: string, record: KeyValuePair, index: number) =>
            <NiInput
              disabled={disabled}
              onChange={(e: any) => {

                const next = cloneDeep(restfulHeaders) || [];
                record.value = e.target.value;
                next[index] = record;
                this.setState({ restfulHeaders: next });
              }}
              value={item}
            />
        },
        {
          title: disabled
            ? '' : <PlusOutlined
              onClick={() => {
                const next = cloneDeep(restfulHeaders) || [];
                next.push({ key: '', value: '' });
                this.setState({ restfulHeaders: next });
              }}
            />,
          className: 'hide',
          key: 'operation',
          width: 50,
          render: (
            operation: any,
            record: KeyValuePair,
            index: number
          ) => {
            return (
              <div className="icon-area">
                {!disabled &&
                  <DeleteOutlined
                    className="delete-icon"
                    onClick={() => {
                      const next = cloneDeep(restfulHeaders) || [];
                      next.splice(index, 1);
                      this.setState({ restfulHeaders: next });
                    }}
                  />
                }
              </div>
            );
          }
        }
      ]}
    />;
  }

  functionMetaComp = () => {

    const { implementationDisabled, functionMeta } = this.state;

    return <NiTable
      pagination={false}
      rowKey="index"
      dataSource={functionMeta.map((item: KeyValuePair, index: number) => ({
        ...item,
        index
      })) || []}
      style={{ flex: 1 }}
      columns={[
        {
          title: 'Key',
          dataIndex: 'key',
          render: (item: string, record: KeyValuePair, index: number) =>
            <NiInput
              disabled={implementationDisabled}
              onChange={(e: any) => {

                const next = cloneDeep(functionMeta) || [];
                record.key = e.target.value;
                next[index] = record;
                this.setState({ functionMeta: next });
              }}
              value={item}
            />
        },
        {
          title: 'Value',
          dataIndex: 'value',
          render: (item: string, record: KeyValuePair, index: number) =>
            <NiInput
              disabled={implementationDisabled}
              onChange={(e: any) => {
                const next = cloneDeep(functionMeta) || [];
                record.value = e.target.value;
                next[index] = record;
                this.setState({ functionMeta: next });
              }}
              value={item}
            />
        },
        {
          title: implementationDisabled
            ? '' : <PlusOutlined
              onClick={() => {

                const next = cloneDeep(functionMeta) || [];

                next.push({
                  key: '',
                  value: ''
                });

                this.setState({ functionMeta: next });
              }}
            />,
          className: 'hide',
          key: 'operation',
          width: 50,
          render: (
            operation: any,
            record: KeyValuePair,
            index: number
          ) => {
            return (
              <div className="icon-area">
                {!implementationDisabled &&
                  <DeleteOutlined
                    className="delete-icon"
                    onClick={() => {
                      const next = cloneDeep(functionMeta) || [];
                      next.splice(index, 1);
                      this.setState({ functionMeta: next });
                    }}
                  />
                }
              </div>
            );
          }
        }
      ]}
    />;
  }

  errorMsgComp = () => {
    const state = this.state;
    const props = this.props;
    const { implementationDisabled } = state;

    return (
      <ReferenceTextArea
        disabled={props.disabled || isStructAgent()}
        rows={1}
        className="refer-input  reset"
        data={
          (state.errorMsgs &&
            state.errorMsgs.length &&
            state.errorMsgs[0]) ||
          ''
        }
        parameterMap={ParameterMapStore.all()}
        initParameters={this.getReferParameters()}
        placeholder={localizedString('data_query_error_msg_popover')}
        style={{
          flex: 1
        }}
        update={(errorMsg: string) => {
          this.setState({ errorMsgs: [errorMsg] });
        }}
      />
    );
  };

  inputTypeComp = () => {
    const state = this.state;
    const props = this.props;
    const { data, availableIntents, availableComposites, availableEntities, externalTypes, genericTypes } = props;
    const { contractDisabled } = state;

    const columns: any[] = [
      {
        title: localizedString('func_param_label', undefined, 'Label'),
        dataIndex: 'name',
        width: '20%',
        render: (item: string, record: FunctionParameter, index: number) => {
          return <NiInput
            disabled={contractDisabled}
            // style={{
            //   width: 200
            // }}
            value={item}
            onChange={(e: any) => {
              const nextInputType = cloneDeep(state.inputParameters);
              record.name = e.target.value;
              nextInputType[index] = record;
              this.setState({ inputParameters: nextInputType });
            }}
            placeholder={localizedString('func_param_name_hint')}
          />;
        }
      },
      {
        title: localizedString('function_input_type'),
        dataIndex: 'type',
        width: '30%',
        render: (item: ReferenceNode | undefined, record: FunctionParameter, index: number) => {
          return <>
            <TypeSelect
              disabled={contractDisabled}
              data={item}
              availableIntents={availableIntents}
              availableComposites={availableComposites}
              availableEntities={availableEntities}
              externalTypes={externalTypes}
              genericTypes={genericTypes}
              frame={props.frame}
              onChange={(next: ReferenceNode) => {
                const nextInputType = cloneDeep(state.inputParameters);
                record.type = next;
                nextInputType[index] = record;
                this.setState({ inputParameters: nextInputType });
              }}
            />
          </>;
        }
      },
      {
        title: localizedString('attribute_multi_value'),
        dataIndex: 'multiValue',
        width: '20%',
        render: (item: boolean, record: FunctionParameter, index: number) => {
          return <Switch
            disabled={contractDisabled}
            checked={item}
            onChange={(checked: boolean) => {
              const nextInputType = cloneDeep(state.inputParameters);
              record.multiValue = checked;
              nextInputType[index] = record;
              this.setState({ inputParameters: nextInputType });
            }} />;
        }
      },
      {
        title: localizedString('data_query_nullable'),
        dataIndex: 'nullable',
        width: '20%',
        render: (item: boolean, record: FunctionParameter, index: number) => {
          return <Switch
            disabled={contractDisabled}
            checked={item}
            onChange={(checked: boolean) => {
              const nextInputType = cloneDeep(state.inputParameters);
              record.nullable = checked;
              nextInputType[index] = record;
              this.setState({ inputParameters: nextInputType });
            }} />;
        }
      },
      {
        title: contractDisabled ? '' : <div className="icon-container">
          <PlusOutlined
            onClick={() => {
              const nextInputType = cloneDeep(state.inputParameters);
              if (nextInputType.find((it: FunctionParameter) =>
                !it.type)) {
                return;
              }
              nextInputType.push({
                name: '',
                multiValue: false,
                nullable: false
              });
              this.setState({ inputParameters: nextInputType });
            }}
          />
        </div>,
        className: 'hide',
        key: 'operation',
        width: 50,
        align: 'right',
        render: (
          operation: any,
          record: FunctionParameter,
          index: number
        ) => {
          return (
            <div className="icon-area">
              {
                <CommentPopover
                  title={`Parameter ${record.name} ${localizedString('comment')}`}
                  value={record.comment}
                  onChange={(value: string) => {
                    const nextInputType = cloneDeep(state.inputParameters);
                    record.comment = value;
                    nextInputType[index] = record;
                    this.setState({ inputParameters: nextInputType });
                  }}
                  disabled={props.disabled}
                />
              }
              {
                !contractDisabled &&
                <DeleteOutlined
                  className="delete-icon"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    const nextInputType = cloneDeep(state.inputParameters);
                    nextInputType.splice(index, 1);
                    this.setState({ inputParameters: nextInputType });
                  }}
                />
              }
            </div>
          );
        }
      }
    ];

    // return <NiTable
    //   style={{ flex: 1 }}
    //   rowKey={(record: FunctionParameter, index?: number) =>
    //     `${index}`}
    //   dataSource={state.inputParameters}
    //   pagination={false}
    //   columns={columns}
    // />
    return <NiDragableTable
      style={{ flex: 1 }}
      rowKey={(record: FunctionParameter, index?: number) =>
        `${index}`}
      dataSource={state.inputParameters}
      pagination={false}
      columns={columns}
      onOrderChange={(next: FunctionParameter[]) => {
        this.setState({ inputParameters: next });
      }}
    />;
  }

  triggerComp = () => {

    const state = this.state;
    const props = this.props;

    const { implementationDisabled } = state;

    return <Switch
      disabled={implementationDisabled}
      checked={state.allowTrigger}
      onChange={(checked: boolean) => {
        if (checked) {
          this.setState({
            allowTrigger: true
          });
        } else {
          this.setState({
            allowTrigger: false
          });
        }
      }}
    />;
  }

  triggerExpressionsComp = () => {

    const state = this.state;
    const props = this.props;
    const { implementationDisabled } = state;

    return <TrainingPhraseComp
      className="trigger-expressions-wrap"
      agentId={getAgentId()}
      frameId={props.frame.id || ''}
      ownerId={props.data.id || ''}
      ownerType={TrainingPhraseOwnerType.CLOSURE}
      phraseType={TrainingPhraseType.QUERY_TRIGGER}
      disableChange={props.disabled || isStructAgent()}
      isSingleLine={false}
      allowClearAll={true}
      data={state.triggerExpressions || []}
      parameterMap={ParameterMapStore.all()}
      parameters={props.selectedParameters}
      update={(values: TrainingPhraseConfig[]) => {
        this.setState({ triggerExpressions: values });
      }}
    />;
  };

  getReferParameters = () => {
    return [
      ...(this.state.inputParameters || [])
        .map((param: FunctionParameter) => ({
          type: ParameterType.FUNCTION_PARAM,
          name: param.name,
          multiValue: param.multiValue,
          proto: param.type as any
        })),
      ...this.props.selectedParameters
    ];
  }

  kotlinRadio = () => {

    const { disabled } = this.props;
    const { kotlin } = this.state;

    return <NiRadio
      disabled={disabled}
      style={{
        marginTop: 4
      }}
      value={kotlin ? 'true' : 'false'}
      onChange={(e: any) => {
        this.setState({
          kotlin: e.target.value === 'true'
        });
      }}
      keyValue={[
        { key: 'false', value: localizedString('function_implementation_provider_dependent') },
        { key: 'true', value: localizedString('function_implementation_kotlin') }
      ]}
    />;
  }

  bodyComp = () => {
    const state = this.state;
    const props = this.props;
    const { data, disabled, frame, availableIntents, availableComposites, availableEntities, externalTypes, genericTypes } = props;
    const { contractDisabled, kotlin, providerType, tab } = state;

    const owner = frame as any;

    const formItems: NiFormItem[] = [];

    formItems.push({
      key: 'output-name',
      label: localizedString('data_query_name'),
      labelExtra: <Space>
        <HelperPopover localizedKey="data_query_name_popover" />
        {
          <CommentPopover
            disabled={disabled}
            title={`${state.outputName} ${localizedString('comment')}`}
            value={state.comment}
            onChange={(value: string) => {
              this.setState({ comment: value });
            }}
          />
        }
      </Space>,
      content: this.outputNameComp()
    });

    // if (this.props.providerType !== ProviderType.Postgrest) {
    //   formItems.push({
    //     key: 'provider-type',
    //     label: localizedString('provider_type'),
    //     labelExtra: <HelperPopover localizedKey="agent_create_provider_type" />,
    //     content: <NiSelect
    //       style={{ width: '100%' }}
    //       disabled={disabled}
    //       className="data-query-select"
    //       placeholder={'Select function type'}
    //       value={(providerType || null) as any}
    //       onChange={(value: string) => {
    //         this.setState({ providerType: value as ProviderType });
    //       }}
    //     >
    //       <NiSelect.Option key={ProviderType.Restful} value={ProviderType.Restful}>
    //         {localizedString('data_query_mode_restful')}
    //       </NiSelect.Option>
    //       {/* <NiSelect.Option key={ProviderType.Postgrest} value={ProviderType.Postgrest}>
    //         {localizedString('data_query_mode_database')}
    //       </NiSelect.Option>
    //       <NiSelect.Option key={ProviderType.GoogleSheets} value={ProviderType.GoogleSheets}>
    //         {localizedString('data_query_mode_google_sheets')}
    //       </NiSelect.Option> */}
    //       <NiSelect.Option key={ProviderType.Native} value={ProviderType.Native}>
    //         Native
    //       </NiSelect.Option>
    //     </NiSelect>
    //   });
    // }


    if (providerType === ProviderType.Postgrest) {
      formItems.push({
        key: 'schedule',
        label: 'Schedule',
        labelExtra: this.scheduleComp(),
        content: this.cronComp()
      });
    }


    formItems.push({
      key: 'input-type',
      label: localizedString('data_query_input_type'),
      content: this.inputTypeComp()
    });

    formItems.push({
      key: 'return-type',
      label: localizedString('data_query_frame'),
      labelExtra: <Space><HelperPopover localizedKey="data_query_frame_popover" /></Space>,
      content: <Form
        name="customized_form_controls"
        layout="inline"
      >
        <Form.Item name="type" label={'Type'}>
          {<TypeSelect
            disabled={contractDisabled}
            data={state.frame as ReferenceNode}
            params={{
              availableIntents,
              availableComposites,
              availableEntities,
              externalTypes,
              genericTypes
            }}
            frame={props.frame}
            onChange={(next: ReferenceNode) => {

              this.setState({
                frame: next
              });
            }}
          />}
        </Form.Item>
        <Form.Item name="multi-value" label={localizedString('attribute_multi_value')}>
          {this.multiValueComp()}
        </Form.Item>
        <Form.Item name="nullable" label={localizedString('data_query_nullable')}>
          {this.nullableComp()}
        </Form.Item>
      </Form>
    });


    if (providerType === ProviderType.Native) {
      formItems.push({
        key: 'static',
        label: localizedString('data_query_static'),
        labelExtra: this.staticComp()
      });
    }
    const implItems: NiFormItem[] = [];

    if (!owner.externalQualification && (providerType === ProviderType.Postgrest ||
      providerType === ProviderType.GoogleSheets ||
      providerType === ProviderType.Restful ||
      providerType === ProviderType.Native)) {
      implItems.push({
        key: 'body',
        label: providerType !== ProviderType.Native ? localizedString('data_query_database_query') : `${localizedString('data_query_database_query')}: kotlin`,
        labelExtra: providerType !== ProviderType.Native && this.kotlinRadio(),
        content: <NiForm
          titleline
          direction="vertical"
          items={[
            providerType === ProviderType.GoogleSheets &&
            !state.kotlin &&
            {
              key: 'function-meta',
              label: localizedString('data_query_function_meta'),
              content: this.functionMetaComp()
            } || undefined,
            providerType === ProviderType.Restful && !state.kotlin && {
              key: 'webhook-url',
              label: 'Method & URI',
              content: this.restfulURLComp()
            } || undefined,
            providerType === ProviderType.Restful && !state.kotlin && {
              key: 'header',
              label: 'Headers',
              content: this.headerComp()
            } || undefined,
            !(providerType === ProviderType.Restful && state.restfulMethod === 'GET' && !state.kotlin) && {
              key: 'query',
              label: kotlin
                ? 'Code'
                : providerType === ProviderType.Restful
                  ? 'Body'
                  : providerType === ProviderType.GoogleSheets
                    ? 'Query'
                    : 'Code',
              content: this.queryComp()
            } || undefined
          ]}
        />
      });
    }

    return (
      <div className="data-query-modal">
        <NiForm
          titleline
          direction="vertical"
          items={tab === 'schema' ? formItems : implItems}
        />
      </div>
    );
  };

  clearState = () => {
    this.setState({
      sqlQuery: '',
      restfulURL: '',
      outputName: ''
    });
  };
}
